import { Spinner } from '@consigli/facade';
import { type MeetingAgreement, type ProjectParticipant } from '@consigli/types';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaTimesCircle } from 'react-icons/fa';
import { toast } from 'react-toastify';

import { CopyButton } from './components';
import { AgreementCard } from './components/agreement-card';
import { TranscriptSourceModal } from './components/transcript-source-modal';
import { useAgreementMutations } from './hooks/use-agreement-mutations';
import { useMeetingRole } from './hooks/use-meeting-role';
import { formatAgreementsForCopy } from './utils';
import { collectCustomParties } from './utils/participant-utils';

import type { ReassignResultItem } from './components/reassign-person-popover';
import type { Meeting, ParsedTranscriptEntry } from './utils/export-types';

interface AgreementProps {
  loading: boolean;
  generating: boolean;
  error: string | null;
  agreements: MeetingAgreement[];
  hasTranscripts: boolean;
  transcriptEntries?: ParsedTranscriptEntry[];
  meeting: Meeting;
  participants: ProjectParticipant[];
  onAgreementsChange: (agreements: MeetingAgreement[]) => void;
  onRetry: (meeting: Meeting) => void;
}

export const Agreement = ({
  loading,
  generating,
  error,
  agreements,
  hasTranscripts,
  transcriptEntries,
  meeting,
  participants,
  onAgreementsChange,
  onRetry,
}: AgreementProps) => {
  const { t } = useTranslation();
  const { updateAgreement, deleteAgreement, isDeleting } = useAgreementMutations(meeting.id);
  const role = useMeetingRole(meeting);
  const [sourceAgreement, setSourceAgreement] = useState<MeetingAgreement | null>(null);

  const customParties = useMemo(
    () => collectCustomParties(agreements.flatMap((a) => a.responsibleParties ?? [])),
    [agreements],
  );

  const handleSaveParties = useCallback(
    async (agreementId: string, result: ReassignResultItem[]): Promise<boolean> => {
      try {
        const updated = await updateAgreement(agreementId, { responsibleParties: result });
        onAgreementsChange(agreements.map((a) => (a.id === agreementId ? updated : a)));
        toast.success(t('meeting-secretary.meetings.responsible-parties-updated'));
        return true;
      } catch {
        toast.error(t('meeting-secretary.meetings.error-updating-responsible-parties'));
        return false;
      }
    },
    [updateAgreement, agreements, onAgreementsChange, t],
  );

  const handleSaveText = useCallback(
    async (agreementId: string, text: string): Promise<boolean> => {
      try {
        const updated = await updateAgreement(agreementId, { agreement: text });
        onAgreementsChange(agreements.map((a) => (a.id === agreementId ? updated : a)));
        toast.success(t('meeting-secretary.meetings.agreement-updated'));
        return true;
      } catch {
        toast.error(t('meeting-secretary.meetings.error-updating-agreement'));
        return false;
      }
    },
    [updateAgreement, agreements, onAgreementsChange, t],
  );

  const handleDelete = useCallback(
    async (agreementId: string) => {
      try {
        await deleteAgreement(agreementId);
        onAgreementsChange(agreements.filter((a) => a.id !== agreementId));
        toast.success(t('meeting-secretary.meetings.agreement-deleted'));
      } catch {
        toast.error(t('meeting-secretary.meetings.error-deleting-agreement'));
      }
    },
    [deleteAgreement, agreements, onAgreementsChange, t],
  );

  return (
    <div className="space-y-4">
      {(loading || generating) && (
        <div className="flex items-center justify-center py-4">
          <Spinner size="xsmall" />
          <span className="ml-2 text-gray-600 text-sm">
            {loading
              ? t('meeting-secretary.meetings.loading-agreements')
              : t('meeting-secretary.meetings.generating-agreements')}
          </span>
        </div>
      )}

      {error && (
        <div className="border rounded-lg p-4 bg-red-50 border-red-200">
          <div className="flex items-start space-x-3">
            <FaTimesCircle className="text-red-500 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="font-medium text-red-800">
                {t('meeting-secretary.meetings.agreements-generation-failed')}
              </h3>
              <p className="text-sm mt-1 text-red-700">{error}</p>
              {hasTranscripts && (
                <button
                  onClick={() => onRetry(meeting)}
                  className="mt-2 text-sm bg-red-100 hover:bg-red-200 text-red-800 px-3 py-1 rounded-md transition-colors"
                >
                  {t('meeting-secretary.meetings.try-again')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {!loading && !generating && !error && agreements.length === 0 && !hasTranscripts && (
        <div className="text-center py-8">
          <div className="text-gray-400 text-4xl mb-4">📝</div>
          <p className="text-gray-500">
            {t('meeting-secretary.meetings.no-transcripts-available')}
          </p>
          <p className="text-sm text-gray-400 mt-2">
            {t('meeting-secretary.meetings.no-transcripts-agreements-required')}
          </p>
        </div>
      )}

      {!loading && !generating && !error && agreements.length === 0 && hasTranscripts && (
        <p className="text-gray-500 text-sm">
          {t('meeting-secretary.meetings.no-agreements-available')}
        </p>
      )}

      {!loading && !generating && !error && agreements.length > 0 && (
        <div className="space-y-3">
          <div className="flex justify-end">
            <CopyButton textToCopy={formatAgreementsForCopy(agreements, t)} />
          </div>
          {agreements.map((agreement) => (
            <AgreementCard
              key={agreement.id}
              agreement={agreement}
              participants={participants}
              customParties={customParties}
              onSaveParties={handleSaveParties}
              onSaveText={handleSaveText}
              onDelete={handleDelete}
              isDeleting={isDeleting}
              onShowSource={transcriptEntries?.length ? setSourceAgreement : undefined}
              warnBeforeChange={agreement.provisional === true && role !== 'organizer'}
            />
          ))}
        </div>
      )}

      <TranscriptSourceModal
        isOpen={!!sourceAgreement}
        onClose={() => setSourceAgreement(null)}
        meeting={meeting}
        entries={transcriptEntries ?? []}
        segmentIds={sourceAgreement?.sourceSegmentIds ?? []}
      />
    </div>
  );
};
